import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  breakfastType,
  dinnerType,
  entertainmentType,
  arrivalType,
  isSplash,
} from '../../utils';
import DailyEvents from '../dailyEvents/DailyEvents';
import EmptyEventCard from '../emptyEventCard/EmptyEventCard';
import { virtualPageView } from '../../googleTagManager';
import Message from '../message/Message';
import ArrivalTimeCard from '../arrivalTimeCard/ArrivalTimeCard';
import './Schedule.scss';
import AppLinksMessage from '../appLinks/AppLinksMessage';

const getEvents = (eventsOnThatDay, showPastBookings) => {
  if (!eventsOnThatDay || eventsOnThatDay.length === 0) return [];
  if (!showPastBookings) return eventsOnThatDay.filter(({ isPastEvent }) => !isPastEvent);
  return eventsOnThatDay;
};

const getHasAvailableItems = (items = [], currentDate) => !!items
  .find(({ date, events: availableEvents }) => date === currentDate && availableEvents.length > 0);

const Schedule = ({
  dailyEvents,
  getAvailableAddons,
  getAvailableEntertainments,
  getBookedEntertainments,
  resId,
  siteCode,
  shouldFetchAvailableAddons,
  shouldFetchAvailableEntertainments,
  shouldFetchBookedEntertainments,
  availableEntertainments,
  splashEntertainments,
  showEmptyBreakfast,
  showEmptyDinner,
  hasPastEvents,
  showEmptyArrival,
  shouldFetchAvailableArrivalTimeSlots,
  getAvailableArrivalTimeSlots,
  arrivalStartTime,
  arrivalEndTime,
  isArrivalTimeEditable,
  playStoreLink,
  appStoreLink,
  resortMessage,
  showAppLinksMessage,
  deleteEntertainment,
  removeDining,
  setArrivalPageWasBooked,
}) => {
  const [showPastBookings, setShowPastBookings] = useState(false);

  useEffect(() => {
    if (shouldFetchAvailableAddons) getAvailableAddons(resId);
    if (shouldFetchBookedEntertainments) getBookedEntertainments(resId, siteCode);
    if (shouldFetchAvailableEntertainments) getAvailableEntertainments(siteCode);
    if (shouldFetchAvailableArrivalTimeSlots) getAvailableArrivalTimeSlots();
  }, [
    getAvailableAddons,
    getAvailableEntertainments,
    getBookedEntertainments,
    resId,
    siteCode,
    shouldFetchAvailableAddons,
    shouldFetchAvailableEntertainments,
    shouldFetchBookedEntertainments,
    shouldFetchAvailableArrivalTimeSlots,
    getAvailableArrivalTimeSlots,
  ]);

  useEffect(() => {
    virtualPageView('plan my day', 'schedule', '/ga-virtual/plan-my-day/schedule');
  }, []);

  const dates = showPastBookings ? Object.keys(dailyEvents) : Object.keys(dailyEvents).filter(dateString => moment(dateString).isSameOrAfter(moment(), 'day'));

  return (
    <div className="schedule">
      <Message messages={['Can’t make a show? Not to worry, please remember to cancel it on here, so that other families can enjoy it!']} />
      {hasPastEvents && (
        <div className="toggle-show-past-bookings" onClick={() => setShowPastBookings(!showPastBookings)}>
          {showPastBookings ? 'Hide past bookings' : 'View past bookings'}
        </div>
      )}
      {showEmptyArrival && <EmptyEventCard type={arrivalType} bookText="Book now" />}
      {showEmptyBreakfast && <EmptyEventCard type={breakfastType} bookText="Book now" />}
      {showEmptyDinner && <EmptyEventCard type={dinnerType} bookText="Book now" />}
      {
        !!arrivalStartTime && !!arrivalEndTime && (
          <ArrivalTimeCard
            arrivalStartTime={arrivalStartTime}
            arrivalEndTime={arrivalEndTime}
            isArrivalTimeEditable={isArrivalTimeEditable}
            setArrivalPageWasBooked={setArrivalPageWasBooked}
          />
        )
      }
      {showAppLinksMessage && (
        <AppLinksMessage
          playStoreLink={playStoreLink}
          appStoreLink={appStoreLink}
          resortMessage={resortMessage}
          positiveButtonText="OK"
        />
      )}
      {
        !!dailyEvents && dates.length > 0 && dates.map((currentDate, index) => {
          const events = getEvents(dailyEvents[currentDate], showPastBookings);
          const hasBookedEntertainment = !!events.find(({ type }) => type === entertainmentType);
          const hasBookedSplash = !!events.find(({ name }) => name === isSplash(name));
          const hasAvailableEntertainments = getHasAvailableItems(availableEntertainments, currentDate);
          const hasSplashEntertainments = getHasAvailableItems(splashEntertainments, currentDate);
          const shouldDisplayEmptyEntertainmentCard = !hasBookedEntertainment && hasAvailableEntertainments;
          const shouldDisplayEmptySplashCard = !hasBookedEntertainment
            && !hasBookedSplash
            && !hasAvailableEntertainments
            && hasSplashEntertainments;
          return (
            <DailyEvents
              events={events}
              currentDateString={currentDate}
              key={index}
              dateIndex={index}
              shouldDisplayEmptyEntertainmentCard={shouldDisplayEmptyEntertainmentCard}
              shouldDisplayEmptySplashCard={shouldDisplayEmptySplashCard}
              deleteEntertainment={deleteEntertainment}
              resId={resId}
              siteCode={siteCode}
              getBookedEntertainments={getBookedEntertainments}
              getAvailableEntertainments={getAvailableEntertainments}
              removeDining={removeDining}
            />
          );
        })
      }
    </div>
  );
};

Schedule.propTypes = {
  getAvailableAddons: PropTypes.func.isRequired,
  getAvailableEntertainments: PropTypes.func.isRequired,
  getBookedEntertainments: PropTypes.func.isRequired,
  dailyEvents: PropTypes.object.isRequired,
  resId: PropTypes.number.isRequired,
  siteCode: PropTypes.string.isRequired,
  shouldFetchAvailableAddons: PropTypes.bool.isRequired,
  shouldFetchAvailableEntertainments: PropTypes.bool.isRequired,
  shouldFetchBookedEntertainments: PropTypes.bool.isRequired,
  availableEntertainments: PropTypes.array.isRequired,
  splashEntertainments: PropTypes.array.isRequired,
  showEmptyBreakfast: PropTypes.bool.isRequired,
  showEmptyDinner: PropTypes.bool.isRequired,
  hasPastEvents: PropTypes.bool.isRequired,
  showEmptyArrival: PropTypes.bool.isRequired,
  shouldFetchAvailableArrivalTimeSlots: PropTypes.bool.isRequired,
  getAvailableArrivalTimeSlots: PropTypes.func.isRequired,
  arrivalStartTime: PropTypes.string,
  arrivalEndTime: PropTypes.string,
  isArrivalTimeEditable: PropTypes.bool.isRequired,
  playStoreLink: PropTypes.string,
  appStoreLink: PropTypes.string,
  resortMessage: PropTypes.string,
  showAppLinksMessage: PropTypes.bool,
  deleteEntertainment: PropTypes.func.isRequired,
  removeDining: PropTypes.func.isRequired,
  setArrivalPageWasBooked: PropTypes.func,
};

export default Schedule;
